import React from 'react';
import { faTableCells } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/beyond-the-spreadsheet.jpg
import beyondSpreadsheetImg from '../../../assets/images/blogs/AI-Workflows-not-spreadsheets.png';

const BeyondTheSpreadsheet = () => (
    <BlogPost
        title="Beyond the Spreadsheet with AI"
        subtitle="The spreadsheet isn't the problem. The mindset is."
        category="Strategy"
        categoryIcon={faTableCells}
        color="#003366"
        date="March 2026"
        readTime="5 min read"
        image={beyondSpreadsheetImg}
        imageAlt="Beyond the spreadsheet - AI-native processes that start from outcomes, not familiar tools"
        ctaHeading="Ready to move beyond the spreadsheet?"
        ctaText="Book a discovery call and we'll show you how to design AI-native processes that start from outcomes, not legacy tools."
        seoDescription="Why AI-ifying Excel and Jira isn't enough, and how starting from outcomes instead of processes transforms operational workflows."
        seoKeywords="AI transformation, operational AI, process design, AI-native workflows, ConsistentAI, AgentDOG, Nebulyx"
        slug="beyond-the-spreadsheet"
    >
        <p>
            With getting value from AI, the spreadsheet isn't the problem. The mindset is.
        </p>

        <p>
            We build a lot of AI use-cases with many orgs and one pattern keeps coming up:
            are we just building faster horses (i.e. AI-ifying Excel/Jira) or are we really
            looking to improve the process?
        </p>

        <p>
            I call this <strong>the Excel workflow problem</strong>.
        </p>

        <h3>The Pattern We Keep Seeing</h3>

        <p>
            We're designing lots of AI-native processes in the regulatory space, including
            for financial back-office teams - risk, finance, compliance, operations, etc.
        </p>

        <p>
            Operationally minded people with tools to match: Excel, tables, forms, clunky
            task-based workflows built for a different era.
        </p>

        <p>
            And at some point the question always comes up: with AI, are we just trying to
            accelerate Excel usage, or are we actually looking to significantly move the
            dial for the business?
        </p>

        <blockquote className="blog-post-quote">
            "It's still asking how we compress the familiar into a new wrapper - and I've
            been guilty of it too."
        </blockquote>

        <h3>The Honest Truth</h3>

        <p>
            When you put AI in front of operational teams, the instinct is to make it
            behave like the tools that got them here. Spreadsheets, Jira boards, status
            columns etc.
        </p>

        <p>
            Familiar shapes for an unfamiliar technology - completely rational, but not
            enough.
        </p>

        <p>
            There is always a tension between driving adoption from existing users and
            getting real value from AI.
        </p>

        <p>
            But to get the latter, you need to really think about the process, outcome,
            and UX to support it.
        </p>

        <h3>How We Approach It</h3>

        <p>
            <strong>Start from the outcome, not the process</strong>, and ask: what would
            this actually look like?
        </p>

        <p>
            Model it first using an AI digital twin (Nebulyx AI Studio does this nicely).
            Design the ideal process unconstrained by legacy.
        </p>

        <p>
            We add <strong>Consistentguardrails</strong> and capture deep domain
            knowledge, using Nebulyx Cognition, our Consistentlanguage model, for
            regulated environments - not as a compromise, but as core part of the
            architecture.
        </p>

        <p>
            We build agents, using <strong>AgentDOG</strong> (our Consistentagent
            framework), to absorb the grunt work in the background.
        </p>

        <p>
            And put humans precisely at the moments where judgment genuinely changes the
            outcome.
        </p>

        <h3>The Interface Transforms</h3>

        <p>
            The interface completely transforms when you think this way. You're not
            managing Excel and Jira.
        </p>

        <p>
            <strong>The AI runs the workflow</strong> with people being surfaced at
            decision gates - with context, with recommendations, and the ability to
            act/review and override.
        </p>

        <h3>The Real Goal</h3>

        <blockquote className="blog-post-quote">
            "The goal shouldn't be faster spreadsheets. It should be better outcomes with
            an operating model that supports them."
        </blockquote>

        <p>
            And you only find it when you stop starting with the process and start with
            the outcome. 🎯
        </p>
    </BlogPost>
);

export default BeyondTheSpreadsheet;